/**
 * useNavPrefs — persists Favorites (pinned page paths) and Recently Visited
 * pages in localStorage for the two-tier navigation + command palette.
 */
import { useState, useCallback, useEffect } from 'react';

const read = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const FAV_KEY = 'nav.favorites';
const REC_KEY = 'nav.recents';
const MAX_RECENTS = 8;

export function useNavPrefs() {
  const [favorites, setFavorites] = useState(() => read(FAV_KEY, []));
  const [recents, setRecents] = useState(() => read(REC_KEY, []));

  useEffect(() => {
    localStorage.setItem(FAV_KEY, JSON.stringify(favorites));
  }, [favorites]);
  useEffect(() => {
    localStorage.setItem(REC_KEY, JSON.stringify(recents));
  }, [recents]);

  const isFavorite = useCallback((path) => favorites.includes(path), [favorites]);

  const toggleFavorite = useCallback((path) => {
    setFavorites((prev) =>
      prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path],
    );
  }, []);

  const pushRecent = useCallback((page) => {
    if (!page || !page.path) return;
    setRecents((prev) => {
      const without = prev.filter((x) => x.path !== page.path);
      return [{ path: page.path, label: page.label, moduleTitle: page.moduleTitle }, ...without].slice(0, MAX_RECENTS);
    });
  }, []);

  return { favorites, recents, isFavorite, toggleFavorite, pushRecent };
}

export default useNavPrefs;
